import React, { useEffect, useMemo, useState } from 'react'
import { useAuthentication } from '../../hooks/useAuthentication'
import { useQuery } from '@apollo/react-hooks'
import { GET_VOUCHERS_ALL } from '../Vouchers/gql'
import { DataTable } from '../table/DataTable'
import { getReportsColumns } from '../table/columns/reportsColumns'
import { Container, Form, Row, Col } from 'react-bootstrap'
import Loader from '../Loader'
import VoucherExpandedRow from '../table/VoucherExpandedRow'
import { Redirect } from 'react-router-dom'
import { hasReportsAccess } from '../../utils'

const Reports = () => {
  const { authState } = useAuthentication()
  const userMajlis = authState.authUser.majlis
  const isNational = userMajlis === 'NATIONAL'
  const isAllowed = hasReportsAccess(authState.authUser.department?.name || '')

  const [selectedJamat, setSelectedJamat] = useState<string>(
    isNational ? 'All' : userMajlis
  )
  const [selectedPeriod, setSelectedPeriod] = useState<string>('All')

  const vouchers = useQuery(GET_VOUCHERS_ALL, {
    context: { headers: { authorization: authState.authToken } },
    skip: !isAllowed,
  })

  useEffect(() => {
    if (isAllowed) {
      vouchers.refetch()
    }
  }, [isAllowed])

  const uniqueJamats = useMemo(() => {
    if (!vouchers.data?.vouchersAll) return []
    const jamats = vouchers.data.vouchersAll
      .map((v: any) => (v.department?.name === 'Missionary' ? 'NATIONAL' : v.jamat))
      .filter((j: any) => j) // Filter out null/undefined
    return Array.from(new Set(jamats)).sort()
  }, [vouchers.data])

  const uniquePeriods = useMemo(() => {
    if (!vouchers.data?.vouchersAll) return []
    const periods = vouchers.data.vouchersAll
      .filter((v: any) => v.expense_period)
      .map((v: any) => v.expense_period)
    return Array.from(new Set(periods)).sort().reverse()
  }, [vouchers.data])

  const filteredVouchers = useMemo(() => {
    if (!vouchers.data?.vouchersAll) return []
    let filtered = vouchers.data.vouchersAll

    const jamatFilter = isNational ? selectedJamat : userMajlis
    if (jamatFilter !== 'All') {
      filtered = filtered.filter((v: any) => {
        const effectiveJamat = v.department?.name === 'Missionary' ? 'NATIONAL' : v.jamat
        return effectiveJamat === jamatFilter
      })
    }

    if (selectedPeriod !== 'All') {
      filtered = filtered.filter((v: any) => {
        if (!v.expense_period) return false
        return v.expense_period.toString() === selectedPeriod
      })
    }

    return filtered
  }, [vouchers.data, selectedJamat, selectedPeriod, isNational, userMajlis])

  if (!isAllowed) {
    return <Redirect to="/panel" />
  }

  if (vouchers.loading) return <Loader />
  if (vouchers.error) return <p>Error: {vouchers.error.message}</p>

  return (
    <Container fluid className="px-0">
      <h1>Reports</h1>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="jamatFilter">
            <Form.Label>Filter by Jamat</Form.Label>
            <Form.Control
              as="select"
              value={isNational ? selectedJamat : userMajlis}
              onChange={(e) => isNational && setSelectedJamat(e.target.value)}
              disabled={!isNational}
            >
              {isNational && <option value="All">All</option>}
              {isNational
                ? uniqueJamats.map((jamat: any) => (
                  <option key={jamat} value={jamat}>
                    {jamat}
                  </option>
                ))
                : (
                  <option value={userMajlis}>{userMajlis}</option>
                )}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="periodFilter">
            <Form.Label>Filter by Period</Form.Label>
            <Form.Control
              as="select"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              <option value="All">All</option>
              {uniquePeriods.map((period: any) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>
      <div style={{ overflowX: 'auto' }}>
        <DataTable
          data={filteredVouchers}
          columns={getReportsColumns()}
          renderSubComponent={(row) => (
            <VoucherExpandedRow voucher={row.original} showPaymentInfo={true} />
          )}
        />
      </div>
    </Container>
  )
}

export default Reports
